/**
 * PwaProvider — Registers Service Worker + renders PWA UI components
 * Spec ref: SPEC-025 (PWA Service Worker),
 *           Feature flags: pwa_service_worker, pwa_install_prompt, pwa_offline_indicator
 *
 * This client component:
 * 1. Registers the Service Worker on mount (if flag enabled)
 * 2. Renders OfflineIndicator (if flag enabled)
 * 3. Renders InstallBanner (if flag enabled)
 *
 * ⚠ TECH DEBT — Build-time vs Runtime Feature Flags:
 * The NEXT_PUBLIC_FF_* environment variables used below are **build-time** constants
 * baked into the Next.js bundle at `next build` time. They CANNOT be toggled at
 * runtime without a redeploy. For true runtime feature-flagging (e.g. A/B tests,
 * gradual rollouts, kill switches), these flags must be fetched from a backend API
 * (e.g. /api/v1/features) on mount or via React Context.
 * Track: TECH-DEBT-FE-008 — migrate PWA flags to runtime feature-flag API.
 */

'use client';

import { useEffect } from 'react';
import { OfflineIndicator } from './OfflineIndicator';
import { InstallBanner } from './InstallBanner';

// Feature flags — read from env at build time
// In production these come from the features API endpoint
const FLAGS = {
  pwaServiceWorker: process.env.NEXT_PUBLIC_FF_PWA_SERVICE_WORKER !== 'false',
  pwaInstallPrompt: process.env.NEXT_PUBLIC_FF_PWA_INSTALL_PROMPT !== 'false',
  pwaOfflineIndicator: process.env.NEXT_PUBLIC_FF_PWA_OFFLINE_INDICATOR !== 'false',
};

export function PwaProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Register Service Worker (SPEC-025 §3.2)
    if (FLAGS.pwaServiceWorker && typeof window !== 'undefined') {
      // P-370 fix: add .catch() to the dynamic import chain — without it,
      // a chunk loading failure (network blip, deploy mid-session, chunk
      // 404 after a new deploy changes chunk hashes) surfaces as an
      // "Uncaught (in promise) ..." console error on every page load.
      // The SW registration is best-effort (PWA still works without it
      // via browser cache) — silencing the unhandled rejection is the
      // correct behavior.
      import('@/lib/sw-register')
        .then(({ registerServiceWorker }) => {
          registerServiceWorker();
        })
        .catch(() => {
          // Best-effort — SW registration failure is non-fatal
        });
    }
  }, []);

  return (
    <>
      {children}
      {FLAGS.pwaOfflineIndicator && <OfflineIndicator />}
      {FLAGS.pwaInstallPrompt && <InstallBanner />}
    </>
  );
}
